import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import { Card } from "./ui/card";
import {
  X,
  DollarSign,
  Calendar,
  Users,
  FileText,
  ExternalLink,
  Building,
  Stethoscope,
  CreditCard,
  Shield,
  AlertTriangle,
  Phone,
  Target,
} from "lucide-react";

interface PolicyUpdate {
  id: string;
  title: string;
  summary: string;
  department: "Billing" | "Finance" | "Clinical" | "Compliance";
  urgency: "High" | "Medium" | "Low";
  timestamp: string;
  impact: string;
  status: "New" | "In Review" | "Implemented";
  effectiveDate: string;
  financialImpact: string;
  affectedDepartments: string[];
  detailedChanges: string[];
  actionItems: Array<{
    id: string;
    description: string;
    dueDate: string;
    priority: "High" | "Medium" | "Low";
    assignedDepartment: string;
  }>;
  complianceChecklist: Array<{
    id: string;
    task: string;
    dueDate: string;
    completed: boolean;
  }>;
  sourceDocuments: Array<{
    title: string;
    url: string;
    type: string;
  }>;
}

interface CMSUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  update: PolicyUpdate | null;
}

export function CMSUpdateModal({
  isOpen,
  onClose,
  update,
}: CMSUpdateModalProps) {
  if (!update) return null;

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case "High":
        return "bg-red-100 text-red-800 border-red-200";
      case "Medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getUrgencyText = (urgency: string) => {
    switch (urgency) {
      case "High":
        return "Significant Information";
      case "Medium":
        return "Notable Information";
      case "Low":
        return "General Information";
      default:
        return "Information";
    }
  };

  const getDepartmentIcon = (dept: string) => {
    switch (dept) {
      case "Billing":
        return <CreditCard className="w-4 h-4" />;
      case "Finance":
        return <DollarSign className="w-4 h-4" />;
      case "Clinical":
        return <Stethoscope className="w-4 h-4" />;
      case "Compliance":
        return <Shield className="w-4 h-4" />;
      default:
        return <Building className="w-4 h-4" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="p-0 overflow-hidden"
        style={{
          width: "1400px",
          height: "850px",
          maxWidth: "90vw", // Don't exceed 90% of screen width
          maxHeight: "90vh", // Don't exceed 90% of screen height
        }}
      >
        {/* Header */}
        <DialogHeader className="p-8 pb-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-4">
              <DialogTitle className="text-xl mb-2 pr-8">
                {update.title}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Detailed information about CMS policy update for
                research and informational purposes only.
              </DialogDescription>
              <div className="flex items-center space-x-3">
                <Badge
                  className={getUrgencyColor(update.urgency)}
                >
                  {getUrgencyText(update.urgency)}
                </Badge>
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-800 border-blue-200"
                >
                  {update.department}
                </Badge>
                <Badge variant="secondary">
                  {update.status}
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        {/* Tabbed Content */}
        <Tabs defaultValue="overview" className="flex-1">
          <div className="px-8 pt-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="detailed-changes">
                Detailed Changes
              </TabsTrigger>
              <TabsTrigger value="professional-consultation">
                Professional Consultation
              </TabsTrigger>
              <TabsTrigger value="implementation-patterns">
                Implementation Patterns
              </TabsTrigger>
              <TabsTrigger value="sources">Sources</TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1 max-h-[50vh]">
            <div className="p-8">
              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Left Column - Main Content */}
                  <div className="lg:col-span-2 space-y-8">
                    {/* Financial Information */}
                    <Card className="p-6 bg-accent/5 border-accent/20">
                      <div className="flex items-center mb-4">
                        <DollarSign className="w-5 h-5 text-accent mr-2" />
                        <h3 className="text-lg">
                          Financial Information
                        </h3>
                      </div>
                      <p className="text-2xl text-accent mb-3">
                        {update.financialImpact}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        amounts mentioned in source documents
                      </p>
                      <p className="text-xs text-muted-foreground mt-2 italic">
                        Professional financial evaluation
                        recommended
                      </p>
                    </Card>

                    {/* Summary & Timeline */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Card className="p-6">
                        <h4 className="mb-4 flex items-center">
                          <FileText className="w-4 h-4 mr-2" />
                          Summary
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {update.summary}
                        </p>
                      </Card>

                      <Card className="p-6">
                        <h4 className="mb-4 flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          Mentioned Timeline
                        </h4>
                        <p className="text-lg mb-3">
                          {update.effectiveDate}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Timeline mentioned in source
                        </p>
                      </Card>
                    </div>

                    {/* Information Patterns Detected */}
                    <Card className="p-6">
                      <h4 className="mb-6">
                        Information Patterns Detected
                      </h4>
                      <ul className="space-y-4">
                        <li className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm">
                            Healthcare facilities typically
                            review EHR template requirements
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm">
                            Organizations commonly evaluate
                            patient survey methodologies
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm">
                            Facilities often coordinate with
                            quality assurance teams
                          </span>
                        </li>
                      </ul>
                    </Card>
                  </div>

                  {/* Right Column - Secondary Content */}
                  <div className="space-y-8">
                    {/* Typically Affects */}
                    <Card className="p-6">
                      <h4 className="mb-6 flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        Typically Affects (Informational)
                      </h4>
                      <div className="space-y-4">
                        {update.affectedDepartments.map(
                          (dept, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-3 p-4 bg-muted/50 rounded-lg"
                            >
                              {getDepartmentIcon(dept)}
                              <span className="text-sm">
                                {dept} (typically)
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-4 italic">
                        Professional evaluation recommended
                      </p>
                    </Card>

                    {/* Professional Consultation */}
                    <Card className="p-6">
                      <h4 className="mb-6">
                        Recommended Professional Consultation
                      </h4>
                      <ul className="space-y-4">
                        <li className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm">
                            Legal compliance review with
                            qualified counsel
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm">
                            Operational impact assessment with
                            facility teams
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm">
                            Financial planning consultation
                          </span>
                        </li>
                        <li className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                          <span className="text-sm">
                            Regulatory interpretation with
                            compliance experts
                          </span>
                        </li>
                      </ul>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              {/* Detailed Changes Tab */}
              <TabsContent
                value="detailed-changes"
                className="space-y-8 mt-0"
              >
                <div className="mb-6">
                  <h3 className="mb-4">Detailed Changes</h3>
                  <Card className="p-6 bg-yellow-50 border-yellow-200">
                    <p className="text-sm text-muted-foreground">
                      Information extracted from source
                      documents. Professional interpretation
                      recommended.
                    </p>
                  </Card>
                </div>

                {/* Section 1 - Quality Measures */}
                <Card className="p-6">
                  <h4 className="mb-6 flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Section 3.2 - Quality Reporting Requirements
                  </h4>
                  <div className="space-y-5">
                    <div className="border-l-4 border-accent pl-6">
                      <p className="text-sm mb-3">
                        <span className="text-accent">
                          Document states:
                        </span>{" "}
                        "Healthcare organizations must implement
                        updated HEDIS measures for quality
                        reporting by January 1, 2025."
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Reference: CMS-2024-QR, Page 47, Section
                        3.2.1
                      </p>
                    </div>
                    <div className="border-l-4 border-accent pl-6">
                      <p className="text-sm mb-3">
                        <span className="text-accent">
                          Regulation specifies:
                        </span>{" "}
                        "Patient experience surveys must
                        incorporate new CAHPS methodology for
                        accurate quality assessment."
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Reference: CMS-2024-QR, Page 52, Section
                        3.2.4
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Section 2 - Documentation Standards */}
                <Card className="p-6">
                  <h4 className="mb-6 flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Section 4.1 - Electronic Health Record
                    Standards
                  </h4>
                  <div className="space-y-5">
                    <div className="border-l-4 border-accent pl-6">
                      <p className="text-sm mb-3">
                        <span className="text-accent">
                          Source indicates:
                        </span>{" "}
                        "EHR templates require modification to
                        capture additional quality metrics for
                        value-based care programs."
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Reference: CMS-2024-QR, Page 78, Section
                        4.1.2
                      </p>
                    </div>
                    <div className="border-l-4 border-accent pl-6">
                      <p className="text-sm mb-3">
                        <span className="text-accent">
                          Document states:
                        </span>{" "}
                        "Clinical documentation workflows must
                        align with updated quality measure
                        specifications."
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Reference: CMS-2024-QR, Page 84, Section
                        4.1.7
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Section 3 - Financial Impact Details */}
                <Card className="p-6">
                  <h4 className="mb-6 flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Section 6.3 - Financial Considerations
                  </h4>
                  <div className="space-y-5">
                    <div className="border-l-4 border-accent pl-6">
                      <p className="text-sm mb-3">
                        <span className="text-accent">
                          Regulation specifies:
                        </span>{" "}
                        "Estimated implementation costs range
                        from $125,000 to $340,000 for mid-size
                        healthcare facilities."
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Reference: CMS-2024-QR, Page 156,
                        Section 6.3.1
                      </p>
                    </div>
                    <div className="border-l-4 border-accent pl-6">
                      <p className="text-sm mb-3">
                        <span className="text-accent">
                          Source indicates:
                        </span>{" "}
                        "Quality assurance staff training
                        requires 40-60 hours per team member for
                        new methodologies."
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Reference: CMS-2024-QR, Page 162,
                        Section 6.3.4
                      </p>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* Professional Consultation Tab */}
              <TabsContent
                value="professional-consultation"
                className="space-y-8 mt-0"
              >
                <div className="mb-6">
                  <h3 className="mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-accent" />
                    Professional Consultation Recommended
                  </h3>
                  <Card className="p-6 bg-blue-50 border-blue-200">
                    <p className="text-sm text-muted-foreground">
                      Expert consultation recommended for operational implementation. 
                      This analysis is for informational purposes only.
                    </p>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Expert Specialization */}
                  <Card className="p-6">
                    <h4 className="mb-6 flex items-center">
                      <Shield className="w-4 h-4 mr-2" />
                      Expert Specialization
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm">Healthcare Attorney</p>
                          <p className="text-xs text-muted-foreground">Legal compliance review</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <Shield className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm">Compliance Consultant</p>
                          <p className="text-xs text-muted-foreground">Operational assessment</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <Users className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm">Regulatory Specialist</p>
                          <p className="text-xs text-muted-foreground">Technical interpretation</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <strong>Typical Urgency:</strong> Within 1 week for operational planning
                      </p>
                    </div>
                  </Card>

                  {/* Common Questions */}
                  <Card className="p-6">
                    <h4 className="mb-6">Common Questions Facilities Ask</h4>
                    <div className="space-y-4">
                      <div className="border-l-4 border-accent pl-4">
                        <p className="text-sm mb-2">What are the specific steps for implementing quality measures?</p>
                        <p className="text-xs text-muted-foreground">Technical implementation guidance needed</p>
                      </div>
                      <div className="border-l-4 border-accent pl-4">
                        <p className="text-sm mb-2">How can we ensure our processes are compliant?</p>
                        <p className="text-xs text-muted-foreground">Compliance verification required</p>
                      </div>
                      <div className="border-l-4 border-accent pl-4">
                        <p className="text-sm mb-2">What are potential risks or common issues?</p>
                        <p className="text-xs text-muted-foreground">Risk assessment needed</p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Preparation Typically Required */}
                <Card className="p-6">
                  <h4 className="mb-6">Preparation Typically Required</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                      <span className="text-sm">Gather current quality reporting documentation</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                      <span className="text-sm">Review existing HEDIS measurement processes</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                      <span className="text-sm">Coordinate with quality assurance and IT departments</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                      <span className="text-sm">Plan staff training and resource allocation</span>
                    </li>
                  </ul>
                  <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-800">
                        <strong>Important Notice:</strong> This analysis is for informational purposes only. 
                        Professional evaluation required for operational decisions.
                      </p>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* Implementation Patterns Tab */}
              <TabsContent
                value="implementation-patterns"
                className="space-y-8 mt-0"
              >
                <div className="mb-6">
                  <h3 className="mb-4 flex items-center">
                    <Target className="w-5 h-5 mr-2 text-accent" />
                    Typical Industry Response Patterns
                  </h3>
                  <Card className="p-6 bg-green-50 border-green-200">
                    <p className="text-sm text-muted-foreground">
                      Information patterns observed across healthcare industry. 
                      Professional consultation recommended for facility-specific planning.
                    </p>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Immediate Actions */}
                  <Card className="p-6">
                    <h4 className="mb-6">Immediate Actions (Commonly Observed)</h4>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-xs text-blue-600">1</span>
                        </div>
                        <div>
                          <p className="text-sm mb-1">Review current quality reporting processes</p>
                          <p className="text-xs text-muted-foreground">Baseline assessment typically conducted</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-xs text-blue-600">2</span>
                        </div>
                        <div>
                          <p className="text-sm mb-1">Begin HEDIS measure preparation</p>
                          <p className="text-xs text-muted-foreground">Technical specifications review</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <span className="text-xs text-blue-600">3</span>
                        </div>
                        <div>
                          <p className="text-sm mb-1">Allocate staff time for compliance project</p>
                          <p className="text-xs text-muted-foreground">Resource planning commonly initiated</p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Commonly Involved Roles */}
                  <Card className="p-6">
                    <h4 className="mb-6">Commonly Involved Roles</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="text-sm">Revenue Cycle Management</span>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                        <Shield className="w-4 h-4 text-blue-600" />
                        <span className="text-sm">Compliance Officers</span>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                        <CreditCard className="w-4 h-4 text-purple-600" />
                        <span className="text-sm">Billing Departments</span>
                      </div>
                      <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                        <Stethoscope className="w-4 h-4 text-red-600" />
                        <span className="text-sm">Quality Assurance Teams</span>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Typical Timeframes */}
                <Card className="p-6">
                  <h4 className="mb-6">Typical Timeframes</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl text-blue-600 mb-2">3-6</div>
                      <div className="text-sm text-blue-800 mb-1">months</div>
                      <div className="text-xs text-muted-foreground">Preparation Phase</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl text-green-600 mb-2">Jan 2025</div>
                      <div className="text-sm text-green-800 mb-1">target</div>
                      <div className="text-xs text-muted-foreground">Implementation Date</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl text-purple-600 mb-2">Monthly</div>
                      <div className="text-sm text-purple-800 mb-1">ongoing</div>
                      <div className="text-xs text-muted-foreground">Monitoring Phase</div>
                    </div>
                  </div>
                </Card>

                {/* Risk Considerations */}
                <Card className="p-6">
                  <h4 className="mb-6">Risk Considerations</h4>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-yellow-800 mb-1">Potential quality score impacts</p>
                        <p className="text-xs text-muted-foreground">Star ratings may be affected by implementation delays</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-red-800 mb-1">Revenue considerations</p>
                        <p className="text-xs text-muted-foreground">Quality bonus payments may be impacted</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <Users className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-blue-800 mb-1">Departmental coordination needs</p>
                        <p className="text-xs text-muted-foreground">Multiple teams typically involved in implementation</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-secondary/50 border border-accent/20 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        <strong>Professional Evaluation Required:</strong> Consult healthcare compliance 
                        experts before implementing operational changes.
                      </p>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              {/* Sources Tab */}
              <TabsContent
                value="sources"
                className="space-y-8 mt-0"
              >
                <div className="mb-6">
                  <h3 className="mb-4">Source Documents</h3>
                  <p className="text-xs text-muted-foreground italic">
                    Professional evaluation of source materials
                    recommended
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {update.sourceDocuments.map((doc, index) => (
                    <Card
                      key={index}
                      className="p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <FileText className="w-4 h-4 text-accent" />
                          <div>
                            <h4 className="text-sm mb-2">
                              {doc.title}
                            </h4>
                            <span className="text-xs text-muted-foreground">
                              {doc.type}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-accent hover:bg-accent/10"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>

        {/* Infervia Disclaimer */}
        <div className="px-8 py-6 border-t border-border">
          <Card className="p-6 bg-secondary/50 border-accent/20">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm mb-3">
                  Important Notice
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Infervia provides AI-powered regulatory
                  information for research purposes only.
                  Healthcare facilities should consult qualified
                  compliance professionals before making
                  operational decisions. Not a substitute for
                  professional legal advice.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Footer Actions */}
        <div className="p-8 border-t border-border">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              className="flex items-center space-x-2 px-6 py-3"
            >
              <Phone className="w-4 h-4" />
              <span>Contact Professional Services</span>
            </Button>
            <Button className="bg-accent hover:bg-accent/90 flex items-center space-x-2 px-6 py-3">
              <ExternalLink className="w-4 h-4" />
              <span>View Source Documents</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}